import { useEffect, useState, type ReactNode } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PlaceholderPanel } from "./PlaceholderPanel";
import {
  CODEX_SUB_TAB_KEY,
  formatCount,
  type BottomDockCount,
  type CodexSubTab,
} from "./shellState";

export type CodexPanels = {
  framework?: ReactNode;
  voice?: ReactNode;
  avatar?: ReactNode;
};

export type CodexCounts = {
  framework?: BottomDockCount;
  voice?: BottomDockCount;
  avatar?: BottomDockCount;
};

export const CodexTabPanel = ({
  workspaceId,
  panels,
  counts,
}: {
  workspaceId?: string;
  panels?: CodexPanels;
  counts?: CodexCounts;
}) => {
  const [sub, setSub] = useState<CodexSubTab>(() => {
    if (typeof window === "undefined") return "framework";
    const stored = window.localStorage.getItem(CODEX_SUB_TAB_KEY(workspaceId));
    if (stored === "framework" || stored === "voice" || stored === "avatar") return stored;
    return "framework";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CODEX_SUB_TAB_KEY(workspaceId), sub);
  }, [sub, workspaceId]);

  const subTabs: { id: CodexSubTab; label: string; count?: BottomDockCount; node?: ReactNode }[] = [
    { id: "framework", label: "Framework", count: counts?.framework, node: panels?.framework },
    { id: "voice", label: "Voice", count: counts?.voice, node: panels?.voice },
    { id: "avatar", label: "Avatars", count: counts?.avatar, node: panels?.avatar },
  ];

  return (
    <Tabs value={sub} onValueChange={(v) => setSub(v as CodexSubTab)} className="flex h-full flex-col">
      <div className="shrink-0 border-b border-border/60 p-2">
        <TabsList className="grid w-full grid-cols-3">
          {subTabs.map((t) => {
            const countLabel = formatCount(t.count);
            return (
              <TabsTrigger key={t.id} value={t.id} className="gap-1.5">
                <span>{t.label}</span>
                {countLabel && (
                  <span className="text-[10px] tabular-nums text-muted-foreground">{countLabel}</span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </div>
      {subTabs.map((t) => (
        <TabsContent key={t.id} value={t.id} className="mt-0 min-h-0 flex-1 overflow-auto">
          {t.node ?? (
            <div className="p-3">
              <PlaceholderPanel
                title={t.label}
                bundle="H2"
                description="Panel not provided."
              />
            </div>
          )}
        </TabsContent>
      ))}
    </Tabs>
  );
};
